import {
  apiBlockCard,
  apiGetCard,
  cardBlockPreparationCleared,
  cardUpdated,
  prepareCardBlock,
  selectedCardBlockPreparations,
  selectedCardBlockPreparing,
} from '@shared-core/features/backend'
import { useEvent } from '@shared-core/hooks/use-event'
import { useAppDispatch, useAppSelector } from '@shared-core/store'
import { normalizeOtpCode } from '@shared-core/utils/otp'
import { useEffect, useRef, useState } from 'react'

export const CARD_BLOCK_OTP_LENGTH = 4

type BlockError = 'error' | 'wrong-code' | 'expired'

export function useCardBlock(cardId: string, status?: string) {
  const dispatch = useAppDispatch()
  const cardBlockPreparations = useAppSelector(selectedCardBlockPreparations)
  const cardBlockPreparing = useAppSelector(selectedCardBlockPreparing)
  const preparation = cardBlockPreparations[cardId] ?? null
  const isPreparing = cardBlockPreparing[cardId] === true
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<BlockError | null>(null)
  const [isBlocking, setIsBlocking] = useState(false)
  const busyRef = useRef(false)
  const mountedRef = useRef(true)
  const reset = useEvent(() => {
    if (preparation && preparation.expiresAt <= Date.now()) dispatch(cardBlockPreparationCleared(cardId))
    setError(null)
  })

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!isBlocking && status !== 'blocking') return
    let active = true
    let timer: ReturnType<typeof setTimeout>
    const poll = async () => {
      try {
        const response = await apiGetCard({ routeParams: { id: cardId } })
        const card = response?.data

        if (!active) return

        if (response?.status === 'ok' && card) {
          dispatch(cardUpdated({ id: cardId, status: card.status }))

          if (card.status !== 'blocking') {
            setIsBlocking(false)
            return
          }
        }
      } catch {
        // Повторяем запрос после временной ошибки сети.
      }

      if (active)
        timer = setTimeout(() => {
          void poll()
        }, 3000)
    }
    void poll()
    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [cardId, dispatch, isBlocking, status])

  // Подготовка отправляет код и сохраняет срок действия операции.
  const prepare = async () => {
    if (busyRef.current || (preparation && preparation.expiresAt > Date.now())) return
    busyRef.current = true
    setIsLoading(true)
    setError(null)
    const preparationResult = await dispatch(prepareCardBlock(cardId)).unwrap()

    if (!preparationResult && mountedRef.current) setError('error')

    busyRef.current = false

    if (mountedRef.current) setIsLoading(false)
  }

  // Статус меняем только после успешного подтверждения на сервере.
  const confirm = async (code: string) => {
    const normalizedCode = normalizeOtpCode(code)
    const isValidCode = /^\d{4}$/.test(normalizedCode)

    if (busyRef.current || !preparation || !isValidCode) return false

    if (Date.now() >= preparation.expiresAt) {
      dispatch(cardBlockPreparationCleared(cardId))
      setError('expired')
      return false
    }
    busyRef.current = true
    setIsLoading(true)
    setError(null)
    try {
      const response = await apiBlockCard({
        routeParams: { id: cardId },
        data: {
          idempotency: preparation.idempotency,
          code: normalizedCode,
        },
      })

      const card = response?.data

      if (response?.status === 'ok' && card && (card.status === 'blocked' || card.status === 'blocking')) {
        dispatch(cardBlockPreparationCleared(cardId))
        dispatch(cardUpdated({ id: cardId, status: card.status }))

        if (mountedRef.current) setIsBlocking(card.status === 'blocking')

        return true
      }

      if (!mountedRef.current) return false

      if (response?.errors.base?.includes('WRONG_SECURITY_CODE')) setError('wrong-code')
      else setError('error')

      if (response?.meta.operationCanceled || response?.errors.base?.includes('IDEMPOTENCY_EXPIRED')) {
        dispatch(cardBlockPreparationCleared(cardId))
        setError('expired')
      }
      return false
    } catch {
      if (mountedRef.current) setError('error')
      return false
    } finally {
      busyRef.current = false

      if (mountedRef.current) setIsLoading(false)
    }
  }

  return { preparation, isLoading: isLoading || isPreparing, error, prepare, confirm, reset }
}
